import { useCallback, useMemo, useState } from "react";

import { parseDate } from "@/utils/formatters";

import type { Point } from "./types";

const DAY_MS = 24 * 60 * 60 * 1000;

export type SelectionState = {
  startMs: number;
  endMs: number;
  pixelMidX: number;
  pixelTopY: number;
};

export type Totals = {
  total: number;
  avgPerPeriod: number;
  isEstimated: boolean;
  forecastOnly: boolean;
};

/**
 * Sum daily demand inside `[startMs, endMs]` from the raw cleansed history and
 * raw forecast mean series. Both inputs are per-day values paired with dates —
 * no smoothing, no per-period rescaling. The `avgPerPeriod` is derived from the
 * total and the range width.
 */
export function computeRangeTotals(
  selection: { startMs: number; endMs: number },
  dailyHistory: Point[],
  dailyForecast: Point[],
  periodDays: number,
  todayMs: number | null,
): Totals {
  const { startMs, endMs } = selection;

  const sumInRange = (
    series: Point[],
    dropAfter: number | null,
    dropBeforeOrAt: number | null,
  ) => {
    let sum = 0;
    for (const p of series) {
      const t = parseDate(p.x).getTime();
      if (dropAfter !== null && t > dropAfter) continue;
      if (dropBeforeOrAt !== null && t <= dropBeforeOrAt) continue;
      if (t < startMs || t > endMs) continue;
      sum += p.y;
    }
    return sum;
  };

  const actualTotal = sumInRange(dailyHistory, todayMs, null);
  const forecastTotal = sumInRange(dailyForecast, null, todayMs);
  const total = actualTotal + forecastTotal;

  const rangeDays = Math.max((endMs - startMs) / DAY_MS, 1);
  const avgPerPeriod = (total * periodDays) / rangeDays;

  return {
    total,
    avgPerPeriod: total === 0 ? 0 : avgPerPeriod,
    isEstimated: forecastTotal > 0,
    forecastOnly: actualTotal === 0 && forecastTotal > 0,
  };
}

/** Owns the live drag-selection state and derives totals from the chart series. */
export function useRangeSelection(params: {
  dailyHistory: Point[];
  dailyForecast: Point[];
  periodDays: number;
  todayMs: number | null;
}) {
  const { dailyHistory, dailyForecast, periodDays, todayMs } = params;
  const [selection, setSelection] = useState<SelectionState | null>(null);

  const onChange = useCallback((sel: SelectionState) => setSelection(sel), []);
  const onEnd = useCallback(() => setSelection(null), []);

  const totals = useMemo<Totals | null>(() => {
    if (!selection) return null;
    return computeRangeTotals(
      selection,
      dailyHistory,
      dailyForecast,
      periodDays,
      todayMs,
    );
  }, [selection, dailyHistory, dailyForecast, periodDays, todayMs]);

  return { selection, totals, onChange, onEnd };
}
